import React, { useEffect, useMemo, useState } from "react";
import { ArrowDownLeft, ArrowUpRight, CalendarDays, CircleDollarSign, ReceiptText, Search } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BottomNav from "../components/BottomNav";
import API from "../services/api";

function Passbook() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [transactions, setTransactions] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => getTodayDate());
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [latestBalance, setLatestBalance] = useState(null);
  const [typeFilter, setTypeFilter] = useState(() => searchParams.get("type") || "all");

  useEffect(() => {
    if (!user?.id) {
      navigate("/login");
      return;
    }

    Promise.all([
      API.get(`/transactions/${user.id}`),
      API.get(`/wallet/${user.id}`).catch(() => ({ data: {} })),
    ])
      .then(([txRes, walletRes]) => {
        setTransactions(Array.isArray(txRes.data) ? txRes.data : []);
        const balance = walletRes?.data?.balance;
        setLatestBalance(balance === null || balance === undefined || balance === "" ? null : balance);
      })
      .catch(() => {
        setTransactions([]);
        setLatestBalance(null);
      });
  }, [navigate, user]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = viewportWidth <= 480;

  const transactionsWithBalances = useMemo(() => {
    const ordered = Array.isArray(transactions)
      ? [...transactions].sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
      : [];

    let runningBalance = latestBalance === null || latestBalance === undefined || latestBalance === ""
      ? null
      : Number(latestBalance);

    return ordered.map((item) => {
      const storedBalance = item.wallet_balance !== null && item.wallet_balance !== undefined && item.wallet_balance !== ""
        ? Number(item.wallet_balance)
        : null;
      const effectiveBalance = storedBalance !== null
        ? storedBalance
        : runningBalance;

      if (effectiveBalance !== null && Number.isFinite(effectiveBalance)) {
        const delta = getWalletEffect(item);
        runningBalance = effectiveBalance - delta;
      }

      return {
        ...item,
        effective_wallet_balance: effectiveBalance,
      };
    });
  }, [latestBalance, transactions]);

  const filtered = transactionsWithBalances.filter((item) => {
    const text = `${item.type || ""} ${item.amount || ""} ${item.status || ""} ${item.remark || ""} ${item.utr || ""} ${item.effective_wallet_balance || ""}`.toLowerCase();
    const matchesDate = !selectedDate || toDateKey(item.created_at) === selectedDate;
    const type = String(item.type || "").toLowerCase();
    let matchesType = true;
    if (typeFilter === "deposit") matchesType = type.includes("deposit") || type.includes("credit") || type.includes("bonus");
    if (typeFilter === "withdraw") matchesType = type.includes("withdraw");
    return matchesDate && matchesType && text.includes(query.toLowerCase());
  });

  const getTransactionMeta = (item) => {
    const amount = Number(item.amount || 0);
    const type = String(item.type || "").toLowerCase();
    const status = String(item.status || "").toLowerCase();
    const isDebit = amount < 0 || type.includes("withdraw") || type.includes("debit") || type.includes("bet");
    const isPending = status === "pending";
    const isWalletTransaction =
      type.includes("withdraw") ||
      type.includes("deposit") ||
      type.includes("debit") ||
      type.includes("credit") ||
      type.includes("bonus") ||
      type.includes("bet") ||
      type.includes("win");

    return {
      isDebit,
      icon: isWalletTransaction ? (
        isDebit ? <ArrowUpRight size={18} /> : <ArrowDownLeft size={18} />
      ) : (
        <CircleDollarSign size={18} />
      ),
      iconWrap: isDebit ? debitIconWrap : creditIconWrap,
      amountColor: isDebit ? "#dc2626" : "#16a34a",
      badge: isPending ? pendingBadge : isDebit ? debitBadge : creditBadge,
      badgeText: isPending ? "Pending" : isDebit ? "Debit" : "Credit",
    };
  };

  return (
    <div style={page}>
      <header style={{ ...topbar, ...(isMobile ? compactTopbar : null) }}>
        <button type="button" style={backButton} onClick={() => navigate("/dashboard")}>{"<"}</button>
        <strong>Passbook</strong>
        <div style={{ ...dateButton, ...(isMobile ? compactHeaderDateButton : null) }}>
          <CalendarDays size={15} />
          <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={{ ...dateInput, ...(isMobile ? compactDateInput : null) }} />
        </div>
      </header>

      <main style={{ ...content, ...(isMobile ? compactContent : null) }}>
        {/* Type filter tabs */}
        <div style={typeTabs}>
          {[["all","All"], ["deposit","Deposits"], ["withdraw","Withdrawals"]].map(([val, label]) => (
            <button key={val} type="button" style={typeFilter === val ? typeTabActive : typeTabBtn} onClick={() => setTypeFilter(val)}>
              {label}
            </button>
          ))}
        </div>
        <div style={{ ...searchBox, ...(isMobile ? compactSearchBox : null) }}>
          <Search size={16} color="#94a3b8" />
          <input
            style={{ ...input, ...(isMobile ? compactInput : null) }}
            placeholder="Search amount, utr or description..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div style={{ ...dateFilterRow, ...(isMobile ? compactDateFilterRow : null) }}>
          <span style={{ ...dateFilterLabel, ...(isMobile ? compactDateFilterLabel : null) }}>Showing transactions for</span>
          <div style={dateActions}>
            <div style={{ ...dateFilterPill, ...(isMobile ? compactDateFilterPill : null) }}>
              <CalendarDays size={14} />
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={{ ...pillDateInput, ...(isMobile ? compactPillDateInput : null) }} />
            </div>
            {selectedDate ? (
              <button type="button" style={{ ...clearButton, ...(isMobile ? compactDateActionButton : null) }} onClick={() => setSelectedDate("")}>
                All Dates
              </button>
            ) : (
              <button type="button" style={{ ...todayButton, ...(isMobile ? compactDateActionButton : null) }} onClick={() => setSelectedDate(getTodayDate())}>
                Today
              </button>
            )}
          </div>
        </div>

        {!filtered.length && (
          <section style={emptyCard}>
            <ReceiptText size={36} color="#cbd5e1" />
            <p>No transactions found</p>
          </section>
        )}

        <div style={{ ...list, gap: isMobile ? "9px" : list.gap }}>
          {filtered.map((item) => {
            const amount = Number(item.amount || 0);
            const bonusAmount = Number(item.bonus_amount || 0);
            const tx = getTransactionMeta(item);
            const walletStampValue = item.effective_wallet_balance;

            return (
              <article key={`${item.type}-${item.id}-${item.created_at}`} style={{ ...card, ...(isMobile ? compactCard : null) }}>
                <div style={leftSide}>
                  <div style={{ ...tx.iconWrap, ...(isMobile ? compactIconWrap : null) }}>{tx.icon}</div>
                  <div style={detailsCol}>
                    <strong style={{ ...titleText, ...(isMobile ? compactTitleText : null) }}>{formatTransactionTitle(item)}</strong>
                    <div style={{ ...badgeRow, ...(isMobile ? compactBadgeRow : null) }}>
                      <span style={{ ...tx.badge, ...(isMobile ? compactBadge : null) }}>{tx.badgeText}</span>
                      {!!item.utr && <span style={{ ...utrBadge, ...(isMobile ? compactBadge : null) }}>UTR {item.utr}</span>}
                    </div>
                    {item.remark ? <p style={{ ...remarkText, ...(isMobile ? compactRemarkText : null) }}>{formatRemark(item.remark)}</p> : null}
                    <p style={{ ...metaText, ...(isMobile ? compactMetaText : null) }}>
                      {item.created_at ? new Date(item.created_at).toLocaleString("en-IN") : "-"}
                    </p>
                  </div>
                </div>
                <div style={rightCol}>
                  {bonusAmount > 0 && (
                    <span style={{ ...bonusStamp, ...(isMobile ? compactBalanceStamp : null) }}>
                      Bonus +{formatMoney(bonusAmount)}
                    </span>
                  )}
                  <strong style={{ ...amountText, ...(isMobile ? compactAmountText : null), color: tx.amountColor }}>
                    {tx.isDebit ? "-" : "+"}{formatMoney(Math.abs(amount))}
                  </strong>
                  {walletStampValue !== null && walletStampValue !== undefined && walletStampValue !== "" && (
                    <span style={{ ...balanceStamp, ...(isMobile ? compactBalanceStamp : null) }}>
                      After {formatMoney(walletStampValue)}
                    </span>
                  )}
                  <span style={{ ...statusText, ...(isMobile ? compactStatusText : null) }}>{item.status}</span>
                </div>
              </article>
            );
          })}
        </div>
      </main>

      <BottomNav />
    </div>
  );
}

function formatMoney(value) {
  return `Rs ${Number(value || 0).toFixed(2)}`;
}

function formatTransactionTitle(item = {}) {
  const raw = String(item.type || "").trim();
  const lower = raw.toLowerCase();
  if (lower.startsWith("bet -")) return "Bet On";
  if (lower.startsWith("win -")) return "Winning";
  return raw
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatRemark(value = "") {
  return String(value || "")
    .replace(/â€¢/g, "-")
    .replace(/â‚¹/g, "Rs")
    .replace(/\s*-\s*/g, " - ")
    .replace(/\s+/g, " ")
    .trim();
}

function getWalletEffect(item) {
  const amount = Number(item.amount || 0);
  const type = String(item.type || "").toLowerCase();
  const status = String(item.status || "").toLowerCase();

  if (status === "pending" || status === "rejected" || status === "failed") return 0;
  if (type.startsWith("bet -")) return amount;
  if (type.startsWith("win -")) return amount;
  if (type.includes("withdraw")) return -Math.abs(amount);
  if (type.includes("debit")) return -Math.abs(amount);
  if (type.includes("deposit") || type.includes("bonus") || type.includes("credit") || type.includes("revert")) {
    return Math.abs(amount);
  }
  return amount;
}

function toDateKey(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toISOString().slice(0, 10);
}

function getTodayDate() {
  return new Date().toISOString().slice(0, 10);
}

const typeTabs = { display: "flex", gap: 8, margin: "10px 0 4px" };
const typeTabBtn = { flex: 1, height: 36, border: "1.5px solid #e2e8f0", borderRadius: 999, background: "#fff", color: "#64748b", fontWeight: 700, fontSize: 13, cursor: "pointer" };
const typeTabActive = { ...typeTabBtn, background: "#312e81", borderColor: "#312e81", color: "#fff", boxShadow: "0 2px 8px rgba(49,46,129,0.22)" };

const page = { minHeight: "100vh", background: "#eef3f9", paddingBottom: "74px" };
const topbar = { width: "min(900px, 100%)", height: "48px", margin: "0 auto", display: "grid", gridTemplateColumns: "40px 1fr auto", alignItems: "center", background: "#fff", padding: "0 14px", boxSizing: "border-box" };
const compactTopbar = { height: 46, padding: "0 10px", gridTemplateColumns: "34px 1fr auto", fontSize: 14 };
const backButton = { border: 0, background: "transparent", fontSize: "22px", color: "#334155" };
const dateButton = { height: "34px", display: "flex", alignItems: "center", gap: "7px", border: 0, borderRadius: "8px", background: "#f8fafc", color: "#334155", padding: "0 12px" };
const compactHeaderDateButton = { height: 30, padding: "0 8px", gap: 5 };
const content = { width: "min(900px, 100%)", margin: "0 auto", padding: "0 14px 16px", boxSizing: "border-box" };
const compactContent = { padding: "0 10px 14px" };
const searchBox = { height: "36px", display: "flex", alignItems: "center", gap: "10px", background: "#fff", border: "1px solid #dbe3ef", borderRadius: "8px", padding: "0 12px", margin: "10px 0 12px" };
const input = { flex: 1, border: 0, outline: 0, background: "transparent" };
const compactSearchBox = { height: 34, margin: "8px 0 9px", padding: "0 10px", gap: 8 };
const compactInput = { fontSize: 13 };
const dateFilterRow = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, margin: "0 0 12px", flexWrap: "wrap" };
const dateFilterLabel = { color: "#64748b", fontSize: 13, fontWeight: 700 };
const compactDateFilterRow = { gap: 8, marginBottom: 9 };
const compactDateFilterLabel = { fontSize: 11 };
const dateActions = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };
const dateFilterPill = { display: "inline-flex", alignItems: "center", gap: 8, minHeight: 38, borderRadius: 999, border: "1px solid #dbe3ef", background: "#fff", color: "#1e3a8a", padding: "0 12px", fontWeight: 800 };
const clearButton = { minHeight: 38, borderRadius: 999, border: "1px solid #dbe3ef", background: "#fff", color: "#475569", padding: "0 12px", fontWeight: 700, cursor: "pointer" };
const todayButton = { minHeight: 38, borderRadius: 999, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#1d4ed8", padding: "0 12px", fontWeight: 700, cursor: "pointer" };
const dateInput = { border: 0, outline: 0, background: "transparent", color: "#334155", fontSize: 12, minWidth: 112 };
const pillDateInput = { border: 0, outline: 0, background: "transparent", color: "#1e3a8a", fontSize: 14, fontWeight: 700, minWidth: 120 };
const compactDateInput = { minWidth: 96, fontSize: 11 };
const compactDateFilterPill = { minHeight: 32, padding: "0 9px", gap: 5 };
const compactPillDateInput = { minWidth: 104, fontSize: 12 };
const compactDateActionButton = { minHeight: 32, padding: "0 10px", fontSize: 11 };
const list = { display: "grid", gap: "12px" };
const card = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "14px", padding: "14px", display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "flex-start" };
const compactCard = { borderRadius: 12, padding: "11px 10px", gap: 8 };
const leftSide = { display: "flex", gap: "12px", minWidth: 0, flex: 1 };
const detailsCol = { minWidth: 0, flex: 1 };
const creditIconWrap = { width: "42px", height: "42px", borderRadius: "14px", background: "#dcfce7", color: "#16a34a", display: "grid", placeItems: "center", flexShrink: 0 };
const debitIconWrap = { width: "42px", height: "42px", borderRadius: "14px", background: "#fee2e2", color: "#dc2626", display: "grid", placeItems: "center", flexShrink: 0 };
const compactIconWrap = { width: 34, height: 34, borderRadius: 11 };
const titleText = { color: "#0f172a", display: "block", fontSize: "15px", fontWeight: 800 };
const compactTitleText = { fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const badgeRow = { marginTop: "6px", display: "flex", gap: 6, flexWrap: "wrap" };
const compactBadgeRow = { marginTop: 4, gap: 4 };
const creditBadge = { display: "inline-flex", alignItems: "center", height: "24px", padding: "0 10px", borderRadius: "999px", background: "#dcfce7", color: "#15803d", fontSize: "12px", fontWeight: 700 };
const debitBadge = { display: "inline-flex", alignItems: "center", height: "24px", padding: "0 10px", borderRadius: "999px", background: "#fee2e2", color: "#dc2626", fontSize: "12px", fontWeight: 700 };
const pendingBadge = { display: "inline-flex", alignItems: "center", height: "24px", padding: "0 10px", borderRadius: "999px", background: "#fef3c7", color: "#b45309", fontSize: "12px", fontWeight: 700 };
const utrBadge = { display: "inline-flex", alignItems: "center", height: "24px", padding: "0 10px", borderRadius: "999px", background: "#eff6ff", color: "#2563eb", fontSize: "12px", fontWeight: 700 };
const compactBadge = { height: 20, padding: "0 7px", fontSize: 10 };
const remarkText = { margin: "8px 0 0", color: "#475569", fontSize: "12px", lineHeight: 1.45 };
const compactRemarkText = { marginTop: 5, fontSize: 11, lineHeight: 1.35 };
const rightCol = { textAlign: "right", flexShrink: 0 };
const amountText = { fontSize: "15px", fontWeight: 800 };
const compactAmountText = { fontSize: 13 };
const bonusStamp = { display: "block", width: "fit-content", marginLeft: "auto", marginBottom: 5, padding: "3px 7px", borderRadius: 999, background: "#fff7ed", color: "#b45309", border: "1px solid #fed7aa", fontSize: 10, fontWeight: 900, whiteSpace: "nowrap" };
const balanceStamp = { display: "block", width: "fit-content", marginLeft: "auto", marginTop: 5, padding: "3px 7px", borderRadius: 999, background: "#f8fafc", color: "#475569", border: "1px solid #e2e8f0", fontSize: 10, fontWeight: 800, whiteSpace: "nowrap" };
const compactBalanceStamp = { fontSize: 9, padding: "2px 6px" };
const metaText = { margin: "6px 0 0", color: "#64748b", fontSize: "12px" };
const compactMetaText = { marginTop: 4, fontSize: 10 };
const statusText = { display: "block", marginTop: "6px", color: "#94a3b8", fontSize: "12px", textTransform: "capitalize" };
const compactStatusText = { marginTop: 4, fontSize: 10 };
const emptyCard = { height: "150px", display: "grid", placeItems: "center", alignContent: "center", gap: "12px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "10px", color: "#334155" };

export default Passbook;
